import React, { useEffect, useRef, useCallback } from 'react';
import { marked } from 'marked';
import { Message } from '../types';

interface MessageListProps {
  messages: Message[];
  isTyping?: boolean;
  isThinking?: boolean;
  streamingText?: string;
  isDarkMode?: boolean;
}

// Configure marked for inline rendering (no <p> tags)
marked.use({
  breaks: true,
  gfm: true,
});

const renderMarkdown = (text: string): string => {
  return marked.parseInline(text) as string;
};

export const MessageList: React.FC<MessageListProps> = ({ messages, isTyping, isThinking, streamingText, isDarkMode }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const wasAtBottomRef = useRef<boolean>(true);

  // Check if user is at or near the bottom of the scroll container
  const isAtBottom = useCallback(() => {
    const container = containerRef.current;
    if (!container) return true;
    
    const threshold = 50; // pixels from bottom to consider "at bottom"
    const scrollBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    return scrollBottom <= threshold;
  }, []);

  // Track scroll position to detect if user is manually scrolling
  const handleScroll = useCallback(() => {
    wasAtBottomRef.current = isAtBottom();
  }, [isAtBottom]);

  // Smart auto-scroll: only scroll if user was already at bottom
  useEffect(() => {
    if (wasAtBottomRef.current && containerRef.current) {
      const container = containerRef.current;
      // Use scrollTop instead of scrollIntoView for better control
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, isTyping, isThinking, streamingText]);

  return (
    <div 
      ref={containerRef}
      onScroll={handleScroll}
      className={`flex-1 overflow-y-auto p-4 space-y-4 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}
    >
      {messages.map((msg) => (
        <div
          key={msg.id}
          className={`flex w-full ${
            msg.sender === 'user' ? 'justify-end' : 'justify-start'
          }`}
        >
          <div
            className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm md:text-base shadow-sm ${
              msg.sender === 'user'
                ? 'bg-blue-500 text-white rounded-br-none'
                : msg.sender === 'system'
                ? `text-xs text-center w-full bg-transparent shadow-none italic ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`
                : isDarkMode
                ? 'bg-gray-700 text-gray-200 border border-gray-600 rounded-bl-none'
                : 'bg-white text-gray-800 border border-gray-200 rounded-bl-none'
            }`}
          >
            {msg.sender === 'system' ? (
              <span>{msg.text}</span>
            ) : (
              <div 
                className="whitespace-pre-wrap leading-relaxed prose prose-sm max-w-none prose-strong:text-inherit prose-em:text-inherit"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.text) }}
              />
            )}
          </div>
        </div>
      ))}

      {/* Show thinking indicator while AI is reasoning */}
      {isThinking && !streamingText && (
        <div className="flex justify-start">
          <div className={`rounded-2xl rounded-bl-none px-4 py-3 shadow-sm flex items-center gap-2 border ${
            isDarkMode 
              ? 'bg-gray-700 border-gray-600' 
              : 'bg-white border-gray-200'
          }`}>
            <div className="relative w-4 h-4">
              <div className={`absolute inset-0 rounded-full border-2 border-t-transparent animate-spin ${
                isDarkMode ? 'border-purple-400' : 'border-purple-500'
              }`}></div>
            </div>
            <span className={`text-sm italic ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
              Thinking...
            </span>
          </div>
        </div>
      )}

      {/* Show streaming text while AI is generating */}
      {streamingText && (
        <div className="flex justify-start">
          <div className={`max-w-[80%] rounded-2xl rounded-bl-none px-4 py-2 text-sm md:text-base shadow-sm border ${
            isDarkMode 
              ? 'bg-gray-700 text-gray-200 border-gray-600' 
              : 'bg-white text-gray-800 border-gray-200'
          }`}>
            <div 
              className="whitespace-pre-wrap leading-relaxed prose prose-sm max-w-none prose-strong:text-inherit prose-em:text-inherit"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(streamingText) }}
            />
          </div>
        </div>
      )}

      {isTyping && !streamingText && (
        <div className="flex justify-start">
          <div className={`rounded-2xl rounded-bl-none px-4 py-3 shadow-sm flex items-center gap-1 border ${
            isDarkMode 
              ? 'bg-gray-700 border-gray-600' 
              : 'bg-white border-gray-200'
          }`}>
            <span className={`block w-2 h-2 rounded-full animate-bounce ${isDarkMode ? 'bg-gray-400' : 'bg-gray-400'}`}></span>
            <span className={`block w-2 h-2 rounded-full animate-bounce [animation-delay:0.2s] ${isDarkMode ? 'bg-gray-400' : 'bg-gray-400'}`}></span>
            <span className={`block w-2 h-2 rounded-full animate-bounce [animation-delay:0.4s] ${isDarkMode ? 'bg-gray-400' : 'bg-gray-400'}`}></span>
          </div>
        </div>
      )}
    </div>
  );
};
